import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Phone, MapPin, Instagram, Facebook, Send, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center py-20" data-testid="contact-success">
        <Card className="max-w-lg mx-auto rounded-3xl border-0 shadow-xl">
          <CardContent className="p-10 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-soar-teal/10 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-soar-teal" />
            </div>
            <h2 className="font-heading font-bold text-2xl text-soar-blue mb-4">
              Message Sent!
            </h2>
            <p className="text-muted-foreground mb-6">
              Thank you for reaching out. We'll get back to you as soon as possible.
            </p>
            <Button
              onClick={() => {
                setSubmitted(false);
                setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
              }}
              className="rounded-full bg-soar-teal hover:bg-soar-teal/90"
              data-testid="send-another-btn"
            >
              Send Another Message
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-24 bg-soar-blue relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-soar-blue via-soar-blue to-soar-blue/90" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-soar-teal/30 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Mail className="w-4 h-4" />
            Get in Touch
          </div>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white mb-6 drop-shadow-lg">
            Contact Us
          </h1>
          <p className="text-lg text-white/90 max-w-3xl mx-auto drop-shadow-md">
            Have questions about our programs? Want to get involved? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <Card className="rounded-3xl border-0 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="font-heading font-bold text-2xl text-soar-blue mb-6">
                    Send Us a Message
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Your Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="rounded-xl"
                          placeholder="John Doe"
                          data-testid="contact-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="rounded-xl"
                          placeholder="john@example.com"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone (Optional)</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          className="rounded-xl"
                          placeholder="(555) 123-4567"
                          data-testid="contact-phone"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="subject">Subject *</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          className="rounded-xl"
                          placeholder="How can we help?"
                          data-testid="contact-subject"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        className="rounded-xl min-h-[150px]"
                        placeholder="Tell us how we can help you..."
                        data-testid="contact-message"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full rounded-full bg-soar-teal hover:bg-soar-teal/90 py-6 text-lg"
                      data-testid="contact-submit-btn"
                    >
                      {submitting ? (
                        "Sending..."
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="rounded-3xl border-0 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="font-heading font-bold text-lg text-soar-blue mb-4">
                    Contact Information
                  </h3>
                  <div className="space-y-4">
                    <a
                      href="mailto:Info@soar-solutions.org"
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted transition-colors group"
                      data-testid="contact-email-link"
                    >
                      <div className="w-10 h-10 rounded-xl bg-soar-teal/10 flex items-center justify-center group-hover:bg-soar-teal transition-colors">
                        <Mail className="w-5 h-5 text-soar-teal group-hover:text-soar-blue transition-colors" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Email</p>
                        <p className="font-medium text-soar-blue">Info@soar-solutions.org</p>
                      </div>
                    </a>

                    <a
                      href="tel:661-401-5031"
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted transition-colors group"
                      data-testid="contact-phone-link"
                    >
                      <div className="w-10 h-10 rounded-xl bg-soar-gold/10 flex items-center justify-center group-hover:bg-soar-gold transition-colors">
                        <Phone className="w-5 h-5 text-soar-gold group-hover:text-soar-blue transition-colors" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Phone</p>
                        <p className="font-medium text-soar-blue">661-401-5031</p>
                      </div>
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-3xl border-0 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="font-heading font-bold text-lg text-soar-blue mb-4">
                    Follow Us
                  </h3>
                  <div className="space-y-3">
                    <a
                      href="https://instagram.com/soar_solutions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted transition-colors group"
                      data-testid="contact-instagram"
                    >
                      <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center group-hover:bg-pink-500 transition-colors">
                        <Instagram className="w-5 h-5 text-pink-500 group-hover:text-soar-blue transition-colors" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Instagram</p>
                        <p className="font-medium text-soar-blue">@soar_solutions</p>
                      </div>
                    </a>

                    <a
                      href="https://facebook.com/SoarSolutionsYouthCenter"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted transition-colors group"
                      data-testid="contact-facebook"
                    >
                      <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                        <Facebook className="w-5 h-5 text-blue-600 group-hover:text-soar-blue transition-colors" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Facebook</p>
                        <p className="font-medium text-soar-blue">Soar Solutions Youth Center</p>
                      </div>
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-3xl border-0 shadow-lg bg-soar-blue text-white">
                <CardContent className="p-6">
                  <h3 className="font-heading font-bold text-lg mb-2">
                    Quick Response
                  </h3>
                  <p className="text-white/80 text-sm">
                    We typically respond to messages within 24-48 hours. 
                    For urgent matters, please call us directly.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
